import { createContext, useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";

/**
 * ReferralContext — captures `?ref=ABCD12` from any URL on the site, validates
 * the code against the backend, and persists it in localStorage for 30 days
 * so the rep gets credit when the visitor checks out later.
 *
 * Persistence key: `ipv_referral_v1`
 * Storage shape: { code: "ABCD12", rep_first_name: "Sarah", expires_at: <iso> }
 */
const ReferralCtx = createContext(null);
const STORAGE_KEY = "ipv_referral_v1";
const TTL_DAYS = 30;

function loadStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj?.code) return null;
    if (obj.expires_at && new Date(obj.expires_at).getTime() < Date.now()) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return obj;
  } catch {
    return null;
  }
}

export function ReferralProvider({ children }) {
  const [referral, setReferral] = useState(loadStored);
  const [searchParams, setSearchParams] = useSearchParams();

  // Capture ?ref=XXX from URL
  useEffect(() => {
    const code = (searchParams.get("ref") || "").toUpperCase().trim();
    if (!code) return;
    if (referral?.code === code) {
      // Already stored — clean the URL so it doesn't keep re-validating
      const next = new URLSearchParams(searchParams);
      next.delete("ref");
      setSearchParams(next, { replace: true });
      return;
    }
    let cancelled = false;
    api
      .get(`/sales-rep/by-code/${code}`)
      .then((r) => {
        if (cancelled) return;
        const expires = new Date(Date.now() + TTL_DAYS * 24 * 3600 * 1000).toISOString();
        const stored = {
          code: r.data.referral_code,
          rep_first_name: r.data.rep_first_name,
          expires_at: expires,
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
        setReferral(stored);
      })
      .catch(() => {
        // Invalid code — silently ignore
      })
      .finally(() => {
        // Clean the URL regardless so refresh doesn't re-trigger
        const next = new URLSearchParams(searchParams);
        next.delete("ref");
        setSearchParams(next, { replace: true });
      });
    return () => {
      cancelled = true;
    };
  }, [searchParams, setSearchParams, referral?.code]);

  const clearReferral = () => {
    localStorage.removeItem(STORAGE_KEY);
    setReferral(null);
  };

  return (
    <ReferralCtx.Provider value={{ referral, clearReferral }}>
      {children}
    </ReferralCtx.Provider>
  );
}

export const useReferral = () => useContext(ReferralCtx);
